// THE THREE EMPTY STATES.
//
// A zone with no rows is telling the operator one of three DIFFERENT things, and collapsing
// them into one grey "No data" is the most common way this product could lie:
//
//   error     the fetch failed. We do not know what is there. Name the endpoint, say
//             explicitly that NOTHING WAS WRITTEN, offer the retry.
//   no-match  the fetch succeeded; the filter excluded everything. Name the filter, offer to
//             clear it. There ARE rows — just not these.
//   empty     the fetch succeeded and there is genuinely nothing. State the schema fact: what
//             would have to happen for a row to exist.
//
// A missing check never renders as a passed check. `error` is not `empty`.
//
// The state sits on a sheet against the field, and its control lives inside that sheet.
import type { ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import './EmptyState.de.js';
import './EmptyState.fr.js';

export type EmptyKind = 'error' | 'no-match' | 'empty';

export interface EmptyStateProps {
  kind: EmptyKind;
  /** One line, sentence case, no terminal period. */
  title: string;
  /**
   * For `error` this must be the SERVER'S OWN message — never a paraphrase. For `no-match` it
   * names the filter doing the excluding. For `empty` it states the schema fact.
   */
  detail: ReactNode;
  onRetry?: () => void;
  onClear?: () => void;
  clearLabel?: string;
  children?: ReactNode;
  className?: string;
}

const TITLE_PREFIX: Record<EmptyKind, string> = {
  error: 'Could not load',
  'no-match': 'Nothing matches',
  empty: 'Nothing here yet',
};

export default function EmptyState({
  kind, title, detail, onRetry, onClear, clearLabel, children, className,
}: EmptyStateProps) {
  const t = useT();
  return (
    <div className={`empty-state-card ${className ?? ''}`.trim()} data-empty-kind={kind}>
      <div className="empty-state">
        <div className="empty-state-title">{title || t(TITLE_PREFIX[kind])}</div>
        <div className="empty-state-desc">{detail}</div>

        {kind === 'error' && (
          <div className="empty-state-desc mt-8">
            {/* The one sentence that makes an error survivable: it says the world did not move. */}
            <em>{t('Nothing was written.')}</em>
          </div>
        )}

        {(onRetry || onClear || children) && (
          <div className="mt-16 flex items-center justify-center gap-8">
            {kind === 'error' && onRetry && (
              <button type="button" className="btn btn-sm" onClick={onRetry}>{t('Try again')}</button>
            )}
            {kind === 'no-match' && onClear && (
              <button type="button" className="btn btn-sm" onClick={onClear}>{clearLabel ?? t('Clear the filter')}</button>
            )}
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
